/**
 * shared.js
 * The vocabulary the inbox screen is built from.
 *
 * Every label here has one job: making a judgement look like a judgement. A
 * sentiment written by a model and a priority chosen by a person appear side
 * by side on this screen, and the reader has to be able to tell which is
 * which — so anything the AI wrote is marked, and anything nobody has judged
 * says so rather than defaulting to "neutral".
 *
 * Dependencies: components/Icons.jsx
 */

import { Check, Chat, Send, Warning, Sparkle, Users, Eye, Close } from '../Icons.jsx';

/** Where an item came from. Only the two this app actually receives on. */
export const KINDS = {
  comment: { label: 'Comment', Icon: Chat, sub: 'on a post' },
  message: { label: 'Message', Icon: Send, sub: 'direct message' },
};

/**
 * What the model judged.
 *
 * `null` is a real state and the most common one: nobody has run triage on it.
 * It is never rendered as "neutral", which is a different claim.
 */
export const SENTIMENT = {
  positive: { label: 'Positive', tone: 'ok' },
  neutral: { label: 'Neutral', tone: 'muted' },
  negative: { label: 'Negative', tone: 'danger' },
};

export const INTENT = {
  question: { label: 'Question', tone: 'accent', Icon: Chat },
  purchase: { label: 'Purchase intent', tone: 'ok', Icon: Users },
  complaint: { label: 'Complaint', tone: 'danger', Icon: Warning },
  praise: { label: 'Praise', tone: 'ok', Icon: Check },
  spam: { label: 'Spam', tone: 'muted', Icon: Close },
  other: { label: 'Other', tone: 'muted', Icon: Eye },
};

/** Chosen by a person, never by the model. */
export const PRIORITY = {
  low: { label: 'Low', tone: 'muted' },
  normal: { label: 'Normal', tone: 'muted' },
  high: { label: 'High', tone: 'warn' },
  urgent: { label: 'Urgent', tone: 'danger' },
};

export const CONTACT_STATUS = {
  unknown: { label: 'Not known', tone: 'muted' },
  lead: { label: 'Lead', tone: 'accent' },
  customer: { label: 'Customer', tone: 'ok' },
  not_customer: { label: 'Not a customer', tone: 'muted' },
};

/** The tabs, in the order the screen offers them. */
export const TABS = [
  { key: 'all', label: 'All', count: 'all' },
  { key: 'comments', label: 'Comments', count: 'comments' },
  { key: 'messages', label: 'Messages', count: 'messages' },
  { key: 'mentions', label: 'Mentions', count: 'mentions' },
  { key: 'reviews', label: 'Reviews', count: 'reviews' },
  { key: 'assigned', label: 'Assigned to me', count: 'assigned' },
  { key: 'unresolved', label: 'Unresolved', count: 'unresolved' },
  { key: 'leads', label: 'Leads', count: 'leads' },
];

/** The two tools that were their own screens until they moved in here. */
export const TOOL_TABS = [
  { key: 'autoreply', label: 'Auto-reply', Icon: Sparkle },
  { key: 'approvals', label: 'Approvals', Icon: Users },
];

/**
 * A lead score as a word.
 *
 * 78/100 means nothing on its own; "high" does. Both are shown, because the
 * number is what the model actually produced and rounding it away would hide
 * how coarse the judgement is.
 */
export function scoreBand(score) {
  if (score === null || score === undefined) return null;
  if (score >= 70) return { label: 'High', tone: 'ok' };
  if (score >= 40) return { label: 'Medium', tone: 'warn' };
  return { label: 'Low', tone: 'muted' };
}

/** "2 hours ago", or a date once it stops being useful. */
export function when(iso) {
  if (!iso) return '';
  const at = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(at.getTime())) return '';

  const mins = Math.round((Date.now() - at.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** The clock time, for a message bubble. */
export const clock = (iso) => {
  if (!iso) return '';
  const at = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return Number.isNaN(at.getTime())
    ? ''
    : at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export const initials = (name) =>
  String(name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

/** The opening words, for a list row. */
export function snip(text, length = 70) {
  const clean = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!clean) return '';
  return clean.length > length ? `${clean.slice(0, length - 1)}…` : clean;
}
